class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.searchTimer = null;
    this.abortController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    this.isInlineSearch = Boolean(this.closest('.template-search, .trc-catalog-discovery'));
    if (this.isInlineSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.isInlineSearch) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      this.showPending();
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  onKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;

    event.preventDefault();
    this.requestSubmit();
  }
  onInput() {
    clearTimeout(this.searchTimer);
    this.abortPendingRequest();

    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.removeAttribute('results');
    this.showPending();
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      this.onChange();
    }, 150);
  }
  showPending() {
    if (!this.isInlineSearch) return;

    document.body.classList.add('predictive-search-open');
    this.setAttribute('pending', 'true');
    this.setAttribute('aria-busy', 'true');
    this.searchContent?.classList.remove('hidden');
  }
  clearPending() {
    this.removeAttribute('pending');
    this.removeAttribute('aria-busy');
  }
  abortPendingRequest() {
    if (!this.abortController) return;

    this.abortController.abort();
    this.abortController = null;
  }
  clear(event = null) {
    event && event.preventDefault();
    clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.abortPendingRequest();
    this.removeAttribute('loading');
    this.removeAttribute('error');
    this.clearPending();
    this.resetButton.classList.remove('btn--loading');
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    if (this.dataset.resourceTypes) {
      url.searchParams.set('resources[type]', this.dataset.resourceTypes);
    }
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    if (this.renderSearchResults(responseText)) {
      this.setAttribute('results', 'true');
    }
    this.removeAttribute('loading');
    this.clearPending();
    this.resetButton.classList.remove('btn--loading');
  }

  renderSectionFromFetch(url) {
    this.abortPendingRequest();
    const controller = new AbortController();
    this.abortController = controller;

    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        if (controller !== this.abortController) return;

        // Process and cache the fetched data
        if (this.renderSearchResults(responseText)) {
          this.cachedMap.set(url, responseText);
          this.setAttribute('results', 'true');
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        if (controller !== this.abortController) return;

        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.clearPending();
        this.abortController = null;
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        return true;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
    return false;
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
